import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import realEstate from '../../../../assets/Images/realestate.png';
import lending from '../../../../assets/Images/lending.png';
import accounting from '../../../../assets/Images/accounting.png';
import taxation from '../../../../assets/Images/taxation.png';
import legal from '../../../../assets/Images/legal.png';
import compliance from '../../../../assets/Images/compliance.png';
import { NavLink } from 'react-router-dom';

const useCaseData = [
  {
    id: 'real-estate',
    title: 'Real Estate',
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#F4F0FF" />
        <path d="M16 36V20H32V36" stroke="#7B61FF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="20" y="28" width="8" height="8" stroke="#7B61FF" strokeWidth="2.5" strokeLinejoin="round" />
        <rect x="20" y="24" width="2" height="2" fill="#7B61FF" />
        <rect x="26" y="24" width="2" height="2" fill="#7B61FF" />
      </svg>
    ),
    lead: 'How Real Estate Companies Benefit from DocuSafe.ai',
    desc: 'Managing property documents, contracts, and client information can be overwhelming for real estate professionals. DocuSafe.ai helps you organize everything efficiently and securely.',
    features: [
      {
        title: 'Centralized Document Organization',
        content: 'Effortlessly upload, categorize, and manage all property-related documents—such as deeds, disclosures, inspection reports, and compliance files—using bulk upload, smart tagging, and folder-based organization. Advanced version control and audit history ensure every file is current and fully traceable.'
      },
      {
        title: 'AI-Powered Search & Automation',
        content: 'Instantly find any document with full-text OCR search and intelligent categorization. AI-driven metadata extraction and document summarization make it easy to retrieve key property details, while automated reminders help teams track deadlines and compliance requirements seamlessly.'
      },
      {
        title: 'Secure Collaboration & Access Control',
        content: 'Enable real-time collaboration among agents, brokers, and clients with shared editing, comments, and role-based approval workflows. Granular permission levels, multi-factor authentication, and user activity monitoring ensure sensitive data is only accessible to authorized personnel.'
      },
      {
        title: 'Automated Compliance & Workflow Efficiency',
        content: 'Simplify regulatory compliance with automated audit trails, document retention policies, and smart notifications for critical tasks. Streamline every step of the property transaction process, reducing manual work and accelerating closings—all while maintaining the highest standards of security and compliance.'
      }
    ],
    benefits: [
      'Effortlessly organize and manage documents with smart tagging, version control, and automated compliance workflows.',
      'Instantly retrieve documents with AI-powered search, metadata extraction, and automated reminders for deadlines',
      'Collaborate securely with role-based access, multi-factor authentication, and real-time approval workflows.',
    ],
    story: {
      quote: 'DocuSafe.ai has revolutionized how we handle property documentation. What used to take hours now takes minutes, and our clients love how quickly we can provide them with information.',
      author: 'Sarah Johnson',
      role: 'Real Estate Agent'
    },
    conclusion: 'These features empower real estate professionals to work smarter, minimize risk, and deliver faster, more efficient property transactions—without the hassle of manual paperwork or compliance worries.',
    color: 'purple',
    image: realEstate
  },
  {
    id: 'lending-and-brokerage',
    title: 'Lending and Brokerage',
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#E6F0FF" />
        <path d="M16 32V20L24 14L32 20V32" stroke="#3B82F6" strokeWidth="2.5" strokeLinejoin="round" />
        <rect x="20" y="28" width="8" height="8" stroke="#3B82F6" strokeWidth="2.5" strokeLinejoin="round" />
      </svg>
    ),
    lead: "How Lending & Brokerage Firms Benefit from DocuSafe.ai",
    desc: "Managing loan applications, client financial documents, and regulatory paperwork can be complex for financial institutions. DocuSafe.ai streamlines this process with secure, efficient document management solutions tailored for lending and brokerage needs.",
    features: [
      {
        title: "Centralized Loan and Client Document Management",
        content: "Effortlessly collect, organize, and manage loan applications, financial statements, KYC documents, and supporting client data with DocuSafe.ai’s bulk upload, smart tagging, and folder-based organization. Version control and detailed audit history ensure every document stays current and fully traceable throughout the lending process."
      },
      {
        title: "Advanced Security and Permission Controls",
        content: "Protect sensitive financial information with robust role-based access control (RBAC), multi-factor authentication, and granular permission settings. User activity monitoring and audit logs provide full visibility into document access and changes, while optional biometric support adds an extra layer of security for critical data."
      },
      {
        title: "AI-Driven Automation and Search",
        content: "Instantly retrieve any document using full-text search with OCR and intelligent categorization. AI-powered metadata extraction and document summarization accelerate due diligence and underwriting, while automated reminders keep teams on track with regulatory deadlines and client follow-ups."
      },
      {
        title: "Seamless Collaboration and Integration",
        content: "Enable secure, real-time collaboration among loan officers, underwriters, and clients with shared editing, comments, and role-based approval workflows. Integrate DocuSafe.ai with your CRM, ERP, and cloud storage solutions to ensure a smooth, unified workflow across all stages of the lending lifecycle."
      }
    ],
    benefits: [
      'Effortlessly manage loan and client documents with organized storage, version control, and audit-ready traceability for compliance.',
      'Ensure data security with role-based access, multi-factor authentication, activity monitoring, and biometric support for sensitive financial information.',
      'Boost efficiency with AI-driven search, automation, and seamless collaboration, integrating with CRM, ERP, and cloud workflows.',
    ],
    story: {
      quote: 'DocuSafe.ai has made managing loan applications and financial documents much easier. The advanced permission controls have helped us secure our data and ensure compliance.',
      author: 'Michael Thompson',
      role: 'Lending Professional'
    },
    conclusion: 'These features empower lending and brokerage firms to minimize manual paperwork, reduce compliance risks, and accelerate loan processing-all while maintaining the highest standards of data security and operational efficiency.',
    color: 'blue',
    image: lending
  },
  {
    id: 'accounting-and-bookkeeping',
    title: 'Accounting and Bookkeeping',
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#FCE7F3" />
        <rect x="16" y="16" width="16" height="16" rx="4" stroke="#EC4899" strokeWidth="2.5" />
        <rect x="20" y="20" width="8" height="8" fill="#EC4899" />
      </svg>
    ),
    lead: "How Accounting & Bookkeeping Teams Benefit from DocuSafe.ai",
    desc: "Managing financial records, ensuring compliance, and maintaining accurate documentation can be time-consuming for accounting professionals. DocuSafe.ai streamlines financial document management with AI-driven automation, enhanced security, and seamless collaboration.",
    features: [
      {
        "title": "Centralized Organization of Financial Records",
        "content": "Seamlessly upload, categorize, and manage all your financial documents—including receipts, invoices, statements, and reports—using bulk upload, smart tagging, and folder-based organization. DocuSafe.ai’s version control and audit history ensure every record is accurate, up-to-date, and fully traceable."
      },
      {
        "title": "AI-Powered Categorization and Data Extraction",
        "content": "Instantly organize and extract key data from receipts, reports, and financial statements with AI-driven categorization, full-text OCR search, and automated metadata extraction. This eliminates manual data entry, reduces errors, and ensures your records are always ready for audits or financial analysis."
      },
      {
        "title": "Enhanced Security and Access Controls",
        "content": "Protect sensitive financial data with role-based access control, multi-factor authentication, and granular permission settings. User activity monitoring and audit logs provide complete visibility and compliance for every document action."
      },
      {
        "title": "Automated Workflows and Real-Time Collaboration",
        "content": "Streamline bookkeeping with automated reminders, smart notifications, and real-time editing and approval workflows. Collaborate effortlessly with your team or clients, while integrations with your accounting software and cloud storage keep your financial operations running smoothly."
      }
    ],
    benefits: [
      'Organize and manage financial records efficiently with centralized storage, smart tagging, version control, and audit-ready document traceability.',
      'Save time and reduce errors with AI-powered data extraction, categorization, and OCR-enabled document search for faster audits.',
      'Enhance data protection and team collaboration through advanced security controls, real-time approvals, and smooth integration with existing tools.'
    ],
    story: {
      quote: 'DocuSafe.ai has made organizing financial records and receipts much easier. The AI-powered categorization has helped us quickly find the information we need.',
      author: 'Emily Davis',
      role: 'Accounting Professional'
    },
    conclusion: 'These features empower accounting and bookkeeping teams to boost accuracy, save time, and maintain compliance-transforming financial document management into a secure, efficient, and intelligent process.',
    color: 'pink',
    image: accounting
  },
  {
    id: 'taxation',
    title: 'Taxation',
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#FFF7ED" />
        <rect x="16" y="16" width="16" height="16" rx="4" stroke="#F97316" strokeWidth="2.5" />
        <path d="M20 24H28" stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M24 20V28" stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    ),
    lead: "How Tax Professionals Benefit from DocuSafe.ai",
    desc: "Managing tax documents, compliance requirements, and critical deadlines can be challenging for tax professionals. DocuSafe.ai provides intelligent document management solutions that simplify these processes with automation, security, and efficiency.",
    features: [
      {
        title: "Centralized Tax Document Organization",
        content: "Effortlessly upload, categorize, and manage all tax-related documents—including returns, filings, supporting schedules, and correspondence—with bulk upload, smart tagging, and folder-based organization. Version control and audit history ensure every document is accurate, up-to-date, and fully traceable for compliance and audit readiness."
      },
      {
        title: "Automated Deadline Tracking & Smart Reminders",
        content: "Never miss a tax deadline with automated notifications and reminders. DocuSafe.ai tracks important filing dates, payment schedules, and compliance milestones, helping tax teams stay organized and proactive throughout the tax cycle."
      },
      {
        title: "AI-Powered Data Extraction & Categorization",
        content: "Instantly extract and categorize key data from receipts, invoices, and financial statements using AI-driven metadata extraction and full-text OCR search. This automation reduces manual data entry, enhances accuracy, and accelerates tax preparation and reconciliation."
      },
      {
        title: "Comprehensive Audit Trails & Version History",
        content: "Maintain transparent, tamper-proof audit trails for every document action, edit, and access. DocuSafe.ai’s audit history and non-configurable edit logs support statutory requirements and simplify audit preparation, ensuring compliance with evolving tax regulations."
      },
      {
        title: "Regulatory Compliance & Secure Access",
        content: "Protect sensitive tax information with role-based access control, multi-factor authentication, and user activity monitoring. Automated document retention policies and secure storage help meet statutory record-keeping requirements and safeguard client data."
      }
    ],
    benefits: [
      'Automated audit trails for tax documents',
      'Version history for tax documents',
      'Quick search and retrieval of tax information',
      'Efficient tax document management'
    ],
    story: {
      quote: 'DocuSafe.ai has helped us maintain tax documents and ensure compliance. The automated audit trails and version history have made it easier to track deadlines and manage tax documents.',
      author: 'Robert Smith',
      role: 'Tax Professional'
    },
    conclusion: 'These features empower lending and brokerage firms to minimize manual paperwork, reduce compliance risks, and accelerate loan processing-all while maintaining the highest standards of data security and operational efficiency.',
    color: 'orange',
    image: taxation
  },
  {
    id: 'legal-services',
    title: 'Legal Services',
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#E6FCE6" />
        <rect x="16" y="16" width="16" height="16" rx="4" stroke="#10B981" strokeWidth="2.5" />
        <path d="M24 20V28" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M20 24H28" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    ),
    lead: "How Law Firms Benefit from DocuSafe.ai",
    desc: "Managing legal documents, ensuring compliance, and collaborating securely are critical challenges for law firms. DocuSafe.ai provides a secure, intelligent document management solution tailored for legal professionals, boosting productivity and safeguarding sensitive data.",
    features: [
      {
        title: "Organize Case Files & Manage Client Documents",
        content: "Effortlessly upload, categorize, and manage all case-related documents—such as client files, court submissions, legal research, and correspondence—using bulk upload, smart tagging, and metadata-based organization. Advanced version control and audit trails ensure every file is up-to-date, traceable, and compliant with legal industry standards."
      },
      {
        title: "AI-Powered Search, Categorization & Data Extraction",
        content: "Instantly locate any document with full-text OCR search and intelligent categorization. AI-driven metadata extraction and document summarization accelerate legal research, due diligence, and case preparation, minimizing manual effort and reducing the risk of oversight."
      },
      {
        title: "Secure Collaboration & Access Control",
        content: "Enable seamless, real-time collaboration among legal teams, clients, and external partners with shared editing, comments, and role-based approval workflows. Granular permission levels, multi-factor authentication, and user activity monitoring ensure sensitive client data remains confidential and accessible only to authorized personnel."
      },
      {
        title: "Automated Compliance & Workflow Efficiency",
        content: "Simplify regulatory compliance with automated audit logs, document retention policies, and smart notifications for critical deadlines or review cycles. Integration with legal practice management software and cloud storage ensures your workflows remain uninterrupted and efficient."
      },
      {
        title: "Additional Use Cases for Legal Services",
        content: "Maintain comprehensive, matter-centric archives for every client and case, supporting quick retrieval and secure sharing of supporting documents. Leverage customizable reporting and analytics to track document usage, monitor team activity, and generate insights for strategic decision-making. Support remote and hybrid work environments with secure, web-based access to all legal files and collaboration tools, ensuring productivity from anywhere."
      }
    ],
    benefits: [
      "Maintain comprehensive, matter-centric archives for every client and case, supporting quick retrieval and secure sharing of supporting documents.",
      "Leverage customizable reporting and analytics to track document usage, monitor team activity, and generate insights for strategic decision-making.",
      "Support remote and hybrid work environments with secure, web-based access to all legal files and collaboration tools, ensuring productivity from anywhere."
    ],
    story: {
      quote: 'DocuSafe.ai has helped us maintain tax documents and ensure compliance. The automated audit trails and version history have made it easier to track deadlines and manage tax documents.',
      author: 'Robert Smith',
      role: 'Tax Professional'
    },
    conclusion: 'These features empower tax professionals to minimize compliance risks, reduce manual workload, and ensure seamless, secure management of all tax documentation.',
    color: 'blue',
    image: legal
  },
  {
    id: 'compliance',
    title: 'Compliance',
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#F4F0FF" />
        <path d="M16 32L24 16L32 32" stroke="#7B61FF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="20" y="28" width="8" height="8" stroke="#7B61FF" strokeWidth="2.5" strokeLinejoin="round" />
      </svg>
    ),
    lead: 'Simplify regulatory compliance with automated document retention, organization, and secure access controls.',
    desc: "Managing regulatory documents, maintaining audit readiness, and mitigating compliance risks are critical for organizations. DocuSafe.ai automates and streamlines compliance processes, ensuring accuracy, security, and efficiency across all regulatory operations.",
    features: [
      {
        title: "Automated Document Retention & Organization",
        content: "Effortlessly manage regulatory documents with DocuSafe.ai’s automated retention policies, bulk upload, smart tagging, and folder-based organization. AI-powered categorization and metadata extraction ensure every policy, report, and compliance record is accurately filed, instantly searchable, and audit-ready."
      },
      {
        title: "Secure Access Controls & Audit Trails",
        content: "Protect sensitive compliance data with advanced role-based access control (RBAC), multi-factor authentication, and granular permission settings. Comprehensive audit trails and version history log every document action, supporting transparency and simplifying regulatory audits."
      },
      {
        title: "Workflow Automation & Smart Notifications",
        content: "Streamline compliance processes with automated approval workflows, real-time collaboration, and smart notifications for critical deadlines or policy updates. AI-driven insights flag potential compliance gaps and ensure timely responses to evolving regulatory requirements."
      },
      {
        // title: "AI-Driven Risk Management & Reporting",
        content: "Leverage AI to monitor regulatory changes, identify non-compliance risks, and automate the generation of compliance reports. Advanced analytics and exportable dashboards provide a clear view of compliance status, helping organizations make informed decisions and proactively address risks."
      },
      {
        title: "Additional Use Cases for Compliance",
        content: "Automate KYC/AML checks and data privacy compliance with intelligent document screening and redaction. Integrate seamlessly with existing compliance and risk management tools for a unified workflow. Support multiple regulatory frameworks (SOX, GDPR, HIPAA, PCI DSS, and more) with flexible, scalable document management."
      }
    ],
    benefits: [
      "Automate KYC/AML checks and data privacy compliance with intelligent document screening and redaction.",
      "Integrate seamlessly with existing compliance and risk management tools for a unified workflow.",
      "Support multiple regulatory frameworks (SOX, GDPR, HIPAA, PCI DSS, and more) with flexible, scalable document management."
    ],
    story: {
      quote: 'DocuSafe.ai has helped us maintain tax documents and ensure compliance. The automated audit trails and version history have made it easier to track deadlines and manage tax documents.',
      author: 'Robert Smith',
      role: 'Tax Professional'
    },
    conclusion: 'These features empower compliance teams to reduce manual workload, minimize risk, and maintain regulatory excellence-ensuring your organization stays ahead in a fast-changing compliance landscape.',
    color: 'purple',
    image: compliance
  },
];

const HomeUseCases = () => {
  const { topic } = useParams();
  const navigate = useNavigate();
  const selected = useCaseData.find(u => u.id === topic);

  if (selected) {
    return (
      <div className="use-case-page">
        {/* Modern Hero Section */}
        <section className="modern-hero" style={{ backgroundColor: `var(--${selected.color}-bg)` }}>
          <div className="container">
            <div className="hero-contentt">
              {/* <div className="hero-text">
              <button 
                className="back-button" 
                onClick={() => navigate('/use-cases')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 4L6 8L10 12" stroke={`var(--${selected.color})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                All Use Cases
              </button>
                <h1>
                 
                  <span className="hero-title">{selected.title}</span>
                </h1>
                <p className="hero-description">{selected.lead}</p>
                
                <div className="hero-actions">
                  <button 
                    className="primary-btn"
                    style={{ backgroundColor: `var(--${selected.color})` }}
                    onClick={() => navigate('/contact')}
                  >
                    Schedule Demo
                  </button>
                  <button 
                    className="secondary-btn"
                    style={{ color: `var(--${selected.color})`, borderColor: `var(--${selected.color})` }}
                    onClick={() => navigate('/pricing')}
                  >
                    View Pricing
                  </button>
                </div>
              </div> */}

              {/* <div className="hero-image">
                <img 
                  src={selected.image} 
                  alt={selected.title} 
                  className="floating-image"
                  onError={e => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400?text=Image+Not+Found'; }}
                />
              </div> */}
            </div>
          </div>
        </section>

        {/* Modern Content Section */}
        <section className="use-case-content">
          <div className="container">
            <div className="content-overview">
              <h2>How {selected.title} Professionals Benefit</h2>
              <p>{selected.desc}</p>
            </div>

            <div className="features-section">
              <h3 className="section-title">Key Features</h3>
              <div className="features-grid">
                {selected.features.map((feature, index) => (
                  <div
                    className="feature-card"
                    key={index}
                    style={{ borderTop: `4px solid var(--${selected.color})` }}
                  >
                    {/* <div className="feature-number">{index + 1}</div> */}
                    <h4>{feature.title}</h4>
                    <p>{feature.content}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="benefits-section">
              <h3 className="section-title">Key Benefits</h3>
              <div className="benefits-list">
                {selected.benefits.map((benefit, index) => (
                  <div className="benefit-item" key={index}>
                    <div className="benefit-icon" style={{ backgroundColor: `var(--${selected.color}-light)` }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.6666 5L7.49992 14.1667L3.33325 10" stroke={`var(--${selected.color})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="conclusion-section">
              <p>{selected.conclusion}</p>
              <NavLink to='/pricing' className="cta-button" style={{ backgroundColor: `var(--${selected.color})` }}>
                <button
                  style={{ backgroundColor: 'transparent', color: 'white', border: 'none' }}
                >
                  Get Started with DocuSafe.ai
                </button>
              </NavLink>
            </div>
          </div>
        </section>

        <style jsx>{`
          :root {
            --purple: #7B61FF;
            --purple-light: #F4F0FF;
            --purple-bg: #F9F7FF;
            
            --blue: #3B82F6;
            --blue-light: #E6F0FF;
            --blue-bg: #F0F7FF;
            
            --pink: #EC4899;
            --pink-light: #FCE7F3;
            --pink-bg: #FDF2F8;
            
            --orange: #F97316;
            --orange-light: #FFF7ED;
            --orange-bg: #FFF8F0;
            
            --green: #10B981;
            --green-light: #E6FCE6;
            --green-bg: #F0FDF4;
          }
          @import url('https://fonts.cdnfonts.com/css/aquatico');

          // .container {
          //   max-width: 1200px;
          //   margin: 0 auto;
          //   padding: 0 24px;
          // }
          
          /* Modern Hero Styles */
          // .modern-hero {
          //   padding: 80px 0;
          //   position: relative;
          //   overflow: hidden;
          // }
          
          .hero-content {
            display: flex;
            flex-direction: column;
            gap: 40px;
          }
          
          .back-button {
            display: flex;
            align-items: center;
            gap: 8px;
            background: none;
            border: none;
            font-weight: 500;
            color: var(--${selected.color});
            cursor: pointer;
            padding: 0;
            transition: opacity 0.3s;
          }
          
          .back-button:hover {
            opacity: 0.8;
          }
          
          .hero-text {
            display: flex;
            flex-direction: column;
            gap: 16px;
            max-width: 600px;
          }
          
          .hero-subtitle {
            font-size: 14px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: var(--${selected.color});
          }
          
          // .hero-title {
          //   font-size: 48px;
          //   font-weight: 700;
          //   line-height: 1.2;
          //   color: #111827;
          //   margin: 0;
          // }
          
          .hero-description {
            font-size: 18px;
            line-height: 1.6;
            color: #4B5563;
            margin: 0;
          }
          
          .hero-actions {
            display: flex;
            gap: 16px;
            margin-top: 16px;
          }
          
          .primary-btn {
            padding: 12px 24px;
            border-radius: 8px;
            border: none;
            font-weight: 500;
            color: white;
            cursor: pointer;
            transition: transform 0.2s, box-shadow 0.2s;
          }
          
          .primary-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
          
          .secondary-btn {
            padding: 12px 24px;
            border-radius: 8px;
            border: 1px solid;
            background: white;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }
          
          .secondary-btn:hover {
            background: rgba(255,255,255,0.8);
          }
          
          .hero-image {
            position: relative;
            margin-top: 40px;
          }
          
          .floating-image {
            width: 100%;
            max-width: 600px;
            border-radius: 16px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            animation: float 6s ease-in-out infinite;
          }
          
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-15px); }
            100% { transform: translateY(0px); }
          }
          
          /* Content Section Styles */
          .use-case-content {
            padding: 80px 0;
          }
          
          .content-overview {
            text-align: center;
           
            margin: 0 auto 64px;
          }
          
          .content-overview h2 {
            font-family: 'Lexend', sans-serif;
            font-size: 2.5rem;
            font-weight: 700;
            margin-bottom: 24px;
            color:rgb(7, 7, 96);
          }
          
          .content-overview p {
            font-size: 18px;
            line-height: 1.7;
            color: #4B5563;
            max-width: 800px;
            margin: 0 auto;
          }
          
          .section-title {
            font-family: 'Lexend', sans-serif;
            font-size: 3rem;
            font-weight: 600;
            margin-bottom: 32px;
            color: var(--${selected.color});
            text-align: center;
          }
          
          /* Features Grid */
          .features-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
            gap: 32px;
            margin-bottom: 80px;
          }
          
          .feature-card {
            background: white;
            border-radius: 12px;
            padding: 32px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.05);
            transition: transform 0.3s, box-shadow 0.3s;
            position: relative;
            overflow: hidden;
          }
          
          .feature-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 12px 32px rgba(0,0,0,0.1);
          }
          
          .feature-number {
            position: absolute;
            top: 16px;
            right: 16px;
            font-size: 48px;
            font-weight: 700;
            color: rgba(0,0,0,0.03);
            line-height: 1;
          }
          
          .feature-card h4 {
            font-size: 20px;
            font-weight: 600;
            margin-bottom: 16px;
            color: #111827;
          }
          
          .feature-card p {
            font-size: 16px;
            line-height: 1.7;
            color: #4B5563;
          }
          
          /* Benefits Section */
          .benefits-section {
            margin-block: 2rem ;
          }
          
          .benefits-list {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 24px;
            max-width: 1000px;
            margin: 0 auto;
          }
          
          .benefit-item {
            display: flex;
            align-items: flex-start;
            gap: 16px;
          }
          
          .benefit-icon {
            width: 40px;
            height: 40px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          
          .benefit-item span {
            font-size: 16px;
            line-height: 1.5;
            color: #4B5563;
          }
          
          /* Testimonial Section */
          .testimonial-section {
            border-radius: 16px;
            padding: 64px;
            margin-bottom: 80px;
            position: relative;
            overflow: hidden;
          }
          
          .testimonial-content {
            position: relative;
            z-index: 1;
            max-width: 800px;
            margin: 0 auto;
            text-align: center;
          }
          
          .quote-icon {
            margin-bottom: 24px;
          }
          
          .testimonial-content blockquote {
            font-size: 24px;
            line-height: 1.6;
            font-weight: 500;
            color: #111827;
            margin-bottom: 24px;
            font-style: italic;
          }
          
          .author-name {
            font-weight: 600;
            color: #111827;
          }
          
          .author-role {
            font-size: 14px;
            color: #6B7280;
          }
          
          /* Conclusion Section */
          .conclusion-section {
            text-align: center;
            max-width: 800px;
            margin: 0 auto;
          }
          
          .conclusion-section p {
            font-size: 18px;
            line-height: 1.7;
            color: #4B5563;
            margin-bottom: 40px;
          }
          
          .cta-button {
            padding: 16px 32px;
            border-radius: 8px;
            border: none;
            font-weight: 500;
            color: white;
            cursor: pointer;
            font-size: 16px;
            transition: transform 0.2s, box-shadow 0.2s;
          }
          
          .cta-button:hover {
            border-radius: 30px;
            // background-color: #ff00f7;
          }
          
          /* Responsive Styles */
          @media (min-width: 768px) {
            .hero-content {
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            }
            
            .hero-image {
              margin-top: 0;
            }
            
            .floating-image {
              max-width: 500px;
            }
          }
          
          @media (max-width: 768px) {
            // .modern-hero {
            //   padding: 60px 0;
            // }
            
            // .hero-title {
            //   font-size: 36px;
            // }
            
            .hero-description {
              font-size: 16px;
            }
            
            .hero-actions {
              flex-direction: column;
            }
            
            .testimonial-section {
              padding: 40px 24px;
            }
            
            .testimonial-content blockquote {
              font-size: 20px;
            }
          }
            .use-cases-title {
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 24px;
  color: #1a202c;
  letter-spacing: -0.02em;
  font-family: 'Lexend', sans-serif;
}
        `}</style>
      </div>
    );
  }

  return (
    <section className="modern-use-cases">
      <div className="container">
        <div className="use-cases-header">
          <h2 className='use-cases-title'>Trusted Across <span className='highlight-pink'>Industries</span></h2>
          <p className='use-cases-subtitle'>Discover how DocuSafe.ai transforms document management for these key sectors</p>
        </div>

        <div className="use-cases-grid">
          {useCaseData.map((u) => (
            <div
              className="use-case-card"
              key={u.id}
              onClick={() => navigate(`/use-cases/${u.id}`)}
            >
              <div className="card-header d-flex align-items-center gap-3">
                <div className={`icon-wrapper ${u.color}`}>{u.icon}</div>
                <h3>{u.title}</h3>
              </div>
              <p className="card-description">{u.lead}</p>

              <button className={`learn-more ${u.color}`} onClick={() => navigate(`/use-cases/${u.id}`)}>Learn More</button>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .modern-use-cases {
          padding: 100px 0;
          background-color: #F9FAFB;
        }
    
        @media (max-width: 768px) {
          .modern-use-cases {
            padding: 60px 0;
          }
          
          .section-header h2 {
            font-size: 32px;
          }
          
          .section-header p {
            font-size: 16px;
          }
        }
      `}</style>
    </section>
  );
};

export default HomeUseCases;